import { PageMetric, DiscoverabilityInfo } from '@blisspointmedia/bpm-types/dist/DigitalAudit';

// Re-exported so existing call sites (e.g. components/SiteHealthView.tsx) can
// keep importing PageMetric/DiscoverabilityInfo from here.
export type { PageMetric, DiscoverabilityInfo };

export interface DetectionHit {
  pattern: string;
  source: string;
  match: string;
  // Which scan(s) produced this hit - set only by a full-scan merge (see
  // mergeDetections in martech-scanner-utils.ts); undefined for a report
  // that has never had a full scan merged in, since there's nothing to
  // distinguish yet.
  seenIn?: ('quick' | 'full')[];
}

/** category -> platform name -> matched evidence */
export type Detections = Record<string, Record<string, DetectionHit[]>>;

/** ID label (e.g. "GTM Container IDs") -> extracted values */
export type ExtractedIds = Record<string, string[]>;

export const HeadlessConfidence = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
} as const;
export type HeadlessConfidenceType = (typeof HeadlessConfidence)[keyof typeof HeadlessConfidence];

export interface HeadlessAssessment {
  verdict: string;
  confidence: HeadlessConfidenceType;
  signals: string[];
}

export interface AccessChecklistItem {
  platform: string;
  request: string;
  how: string;
}

export const InsightSeverity = {
  RISK: 'RISK',
  OPPORTUNITY: 'OPPORTUNITY',
  NOTE: 'NOTE'
} as const;
export type InsightSeverityType = (typeof InsightSeverity)[keyof typeof InsightSeverity];

export interface AuditInsight {
  severity: InsightSeverityType;
  title: string;
  note: string;
  // Optional "why this matters" detail attached at the view layer
  // (see components/audit-report-utils.ts), mirroring app.py's enrich_insights.
  more?: string;
}

/** One fetched artifact (page HTML, inline script, JS bundle, headers, cookies, ...). */
export interface EvidenceBlob {
  source: string;
  text: string;
}

export interface ScanResult {
  url: string;
  domain: string;
  scannedAt: string;
  pagesScanned: string[];
  jsFilesScanned: number;
  detections: Detections;
  extractedIds: ExtractedIds;
  headlessAssessment: HeadlessAssessment;
  accessChecklist: AccessChecklistItem[];
  advertiserInsights: AuditInsight[];
  // Per-page load metrics from the crawl; optional so scans persisted before
  // the backend shipped the field deserialize cleanly.
  pageMetrics?: PageMetric[];
  // SEO/AI discoverability facts; optional for the same deploy-window reason.
  discoverability?: DiscoverabilityInfo;
  // JS-rendering dependency, computed from evidence at scan time
  // (engine/site-health-utils.ts) since evidence blobs aren't persisted.
  // Inline `import()` type (not a top-level import) is deliberate: site-health-utils.ts
  // imports EvidenceBlob from this file, so a normal import here would be circular.
  renderSummary?: import('./site-health-utils').RenderSummary;
  errors: string[];
  // Individual tracking calls decoded from the evidence (and runtime capture
  // when present) — see engine/tag-fire-utils.ts. Optional so persisted scans
  // from before this field deserialize cleanly.
  tagFires?: import('./tag-fire-constants').TagFire[];
  // Populated by the (not yet built) runtime-scan endpoint — a headless
  // browser's network capture, used by the journey mapper's evidence text
  // and, eventually, a "Runtime-Confirmed Pixel Fires" detection category.
  runtimeNetwork?: {
    thirdPartyHosts: string[];
    firstPartyHosts: string[];
    // Full captured request URLs — lets tag-fire-utils decode on-the-wire
    // parameters. The reference tool's runtime scanner already collects
    // these (network_urls); the backend passes them through when available.
    requests?: string[];
    // Redacted window.dataLayer snapshots, pre- and post-consent - raw
    // material for dataLayerVariables below, kept here for debugging
    // visibility alongside the rest of the runtime capture.
    dataLayerSnapshot?: unknown[];
    postConsentDataLayerSnapshot?: unknown[];
  };
  // Inventory of tagging-relevant variables (dataLayer pushes, GTM container
  // variable definitions, a live runtime snapshot when available) cross-
  // referenced against which tags actually consume them - see
  // engine/datalayer-inventory-utils.ts. Optional for the same deploy-window
  // reason as tagFires/renderSummary. Inline import avoids a circular
  // import (datalayer-inventory-constants.ts doesn't need anything from
  // this file, but keeping the pattern consistent with renderSummary/tagFires).
  dataLayerVariables?: import('./datalayer-inventory-constants').DataLayerVariableInventory;
}

export const GOOGLE_TAG_HOSTS = [
  'googletagmanager.com',
  'google-analytics.com',
  'googleadservices.com',
  'googlesyndication.com'
];

/**
 * Path fragments that mark a script as a CMS/plugin-bundled asset rather than
 * a tagging endpoint. Drupal's `google_tag` contrib module, the WordPress GTM
 * plugins and their equivalents ship a *local copy of the standard GTM loader*
 * inside their own module directory (e.g.
 * `/modules/contrib/google_tag/js/gtm.js`). That is a static file served
 * first-party — not a server-side tagging container.
 */
export const CMS_ASSET_PATH_SEGMENTS = [
  '/modules/',
  '/wp-content/',
  '/wp-includes/',
  '/plugins/',
  '/themes/',
  '/sites/default/files/',
  '/static/',
  '/assets/'
];

export const TRADITIONAL_CMS = new Set([
  'WordPress',
  'Shopify',
  'Wix',
  'Squarespace',
  'Webflow',
  'Drupal',
  'Joomla',
  'Magento / Adobe Commerce',
  'BigCommerce',
  'Salesforce Commerce Cloud (SFCC)',
  'HubSpot CMS',
  'Duda',
  'Ghost',
  'Kajabi',
  'ClickFunnels',
  'WooCommerce (on WordPress)'
]);

export const FRAMEWORKS_HEADLESS_CAPABLE = new Set([
  'Next.js',
  'Gatsby',
  'Nuxt (Vue)',
  'SvelteKit',
  'Astro',
  'Remix',
  'React (generic)'
]);
